/**
 * The mesh's webhooks, on the Settings tab after Keys (docs/mesh-webhooks.md): where mesh
 * changes are posted, which events each takes, and how delivery stands. A new webhook's signing
 * secret is shown once, with a copy button, and nowhere afterwards.
 */

import { MESH_WEBHOOK_GROUPS, type MeshWebhookDeliveryView, type MeshWebhookView, type MeshWebhooksResponse } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { Ellipsis } from '@gravity-ui/icons';
import { Button, Dropdown, Label, Tooltip } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { FormModal } from './cloud/FormModal.tsx';
import { ConfirmDialog, ErrorNote, inputClass, ListBlock, ListGroup, StatusMark, Tick, type ArtTone } from './ui.tsx';

type Catalogue = MeshWebhooksResponse['events'];
type Dialog = { kind: 'add' } | { kind: 'events'; hook: MeshWebhookView } | { kind: 'delete'; hook: MeshWebhookView } | { kind: 'deliveries'; hook: MeshWebhookView };

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** The state as a dot and a word. */
function standing(h: MeshWebhookView): { state: 'online' | 'error' | 'disabled' | 'idle'; label: string } {
  if (h.state === 'disabled') return { state: 'disabled', label: 'Off' };
  if (h.state === 'failing') return { state: 'error', label: 'Failing' };
  return h.lastDeliveryAt ? { state: 'online', label: 'Delivering' } : { state: 'idle', label: 'Waiting for an event' };
}

/** Event checkboxes, grouped; at least one must stay ticked. */
function EventPicker({ catalogue, value, onChange }: { catalogue: Catalogue; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="grid gap-3">
      {MESH_WEBHOOK_GROUPS.map((g) => (
        <fieldset key={g} className="grid gap-1.5">
          <legend className="mb-1 text-xs font-medium text-muted">{g}</legend>
          {catalogue
            .filter((e) => e.group === g)
            .map((e) => (
              <Tick key={e.type} checked={value.includes(e.type)} onChange={(on) => onChange(on ? [...value, e.type] : value.filter((t) => t !== e.type))}>
                <span className="text-sm">{e.text}</span>
              </Tick>
            ))}
        </fieldset>
      ))}
    </div>
  );
}

function AddOrEdit({ dialog, catalogue, onClose, onDone }: { dialog: Extract<Dialog, { kind: 'add' | 'events' }>; catalogue: Catalogue; onClose: () => void; onDone: () => void }) {
  const editing = dialog.kind === 'events' ? dialog.hook : undefined;
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(editing?.events ?? ['nodeCreated', 'nodeDeleted', 'nodeKeyExpiringInLessThan24Hours', 'nodeKeyExpired']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [secret, setSecret] = useState<string>();
  const [copied, setCopied] = useState(false);

  if (secret)
    return (
      <FormModal
        isOpen
        onClose={() => {
          onDone();
          onClose();
        }}
        title="Webhook added"
        blurb="Use this signing secret to check that a request really came from AIOE. It is not shown again, so keep it somewhere safe now."
        submitLabel="Done"
        onSubmit={onClose}
        footer={
          <Button
            variant="primary"
            onPress={() => {
              onDone();
              onClose();
            }}
          >
            Done
          </Button>
        }
      >
        <div className="flex items-center gap-2">
          <code className="mono min-w-0 flex-1 select-all break-all rounded-lg bg-surface-secondary px-3 py-2 text-sm">{secret}</code>
          <Button
            size="sm"
            variant="secondary"
            onPress={() => {
              void navigator.clipboard?.writeText(secret).then(() => setCopied(true), () => undefined);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </FormModal>
    );

  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={editing ? 'Edit events' : 'Add a webhook'}
      blurb={editing ? `Which events are posted to ${editing.host}.` : 'AIOE posts the events you pick to this address, signed, as a list in JSON.'}
      submitLabel={editing ? 'Save' : 'Add webhook'}
      canSubmit={events.length > 0 && (!!editing || /^https?:\/\/\S+$/.test(url.trim()))}
      busy={busy}
      error={error}
      onSubmit={() => {
        setBusy(true);
        setError(undefined);
        const done = editing
          ? routes.updateMeshWebhook(editing.id, { events }).then(() => {
              onDone();
              onClose();
            })
          : routes.createMeshWebhook({ url: url.trim(), events }).then((made) => setSecret(made.secret));
        done.catch((e: unknown) => setError(problemText(e))).finally(() => setBusy(false));
      }}
    >
      {!editing && (
        <label className="grid gap-1 text-sm">
          <span className="text-muted">Address</span>
          <input className={inputClass} type="url" inputMode="url" placeholder="https://example.com/hooks/mesh" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
        </label>
      )}
      <EventPicker catalogue={catalogue} value={events} onChange={setEvents} />
    </FormModal>
  );
}

function Deliveries({ hook, onClose }: { hook: MeshWebhookView; onClose: () => void }) {
  const log = useLoad(() => routes.meshWebhookDeliveries(hook.id), [hook.id]);
  const rows: MeshWebhookDeliveryView[] = log.data?.deliveries ?? [];
  return (
    <FormModal isOpen onClose={onClose} title="Recent deliveries" blurb={`The last 20 posts to ${hook.host}.`} submitLabel="Done" onSubmit={onClose} footer={<Button variant="primary" onPress={onClose}>Done</Button>}>
      <ErrorNote error={log.error} />
      <ul className="divide-y divide-separator text-sm" role="list">
        {rows.map((d) => (
          <li key={d.id} className="flex items-baseline justify-between gap-3 py-2">
            <span className="min-w-0">
              <span className="block truncate">{d.test ? 'Test event' : d.events.join(', ')}</span>
              {d.error && <span className="block text-xs text-muted">{d.error}</span>}
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <StatusMark state={d.ok ? 'online' : 'error'} label={d.ok ? 'Accepted' : 'Failed'} />
              <time className="text-xs text-muted" dateTime={d.at} title={dateTime(d.at)}>
                {timeAgo(d.at)}
              </time>
            </span>
          </li>
        ))}
        {rows.length === 0 && !log.loading && <li className="py-2 text-muted">Nothing has been posted to this webhook yet.</li>}
      </ul>
    </FormModal>
  );
}

export function MeshWebhooks({ tone: _tone }: { tone: ArtTone }) {
  const list = useLoad(() => routes.meshWebhooks(), []);
  const [dialog, setDialog] = useState<Dialog>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<{ ok: boolean; text: string }>();
  const hooks = list.data?.webhooks ?? [];
  const catalogue = list.data?.events ?? [];
  const textOf = (type: string) => catalogue.find((e) => e.type === type)?.text ?? type;
  const run = (f: () => Promise<unknown>) => {
    setError(undefined);
    f().then(
      () => list.reload(),
      (e: unknown) => setError(problemText(e)),
    );
  };
  const act = (hook: MeshWebhookView, key: string) => {
    setNotice(undefined);
    if (key === 'test')
      run(async () => {
        const r = await routes.testMeshWebhook(hook.id);
        setNotice({ ok: r.ok, text: r.message });
      });
    else if (key === 'toggle') run(() => routes.updateMeshWebhook(hook.id, { enabled: !hook.enabled }));
    else if (key === 'events' || key === 'delete' || key === 'deliveries') setDialog({ kind: key, hook });
  };
  return (
    <ListGroup title="Webhooks" count={hooks.length} description="Post mesh changes to another system as they happen, signed so it can check they are ours." actions={
        <Button size="sm" variant="primary" onPress={() => setDialog({ kind: 'add' })}>
          Add webhook
        </Button>
      }><ListBlock flush>
      <ErrorNote error={list.error ?? error} />
      {notice && (
        <div role="status" className={`mx-4 mt-3 rounded-lg border px-3 py-2 text-sm ${notice.ok ? 'border-separator bg-surface-secondary' : 'border-danger/40 bg-danger/10 text-danger'}`}>
          {notice.text}
        </div>
      )}
      <ul className="divide-y divide-separator text-sm" role="list">
        {hooks.map((h) => {
          const st = standing(h);
          return (
            <li key={h.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0">
                <span className="mono block truncate font-medium" title={h.url}>
                  {h.url.replace(/^https?:\/\//, '')}
                </span>
                <span className="block truncate text-xs text-muted">
                  <Tooltip delay={300}>
                    <button type="button" className="underline decoration-dotted underline-offset-2">
                      {plural(h.events.length, 'event')}
                    </button>
                    <Tooltip.Content>
                      <span className="grid gap-0.5">
                        {h.events.map((t) => (
                          <span key={t}>{textOf(t)}</span>
                        ))}
                      </span>
                    </Tooltip.Content>
                  </Tooltip>
                  {' · '}
                  {h.state === 'failing' && h.lastError ? h.lastError : h.state === 'disabled' && h.disabledReason ? h.disabledReason : h.lastDeliveryAt ? `last delivered ${timeAgo(h.lastDeliveryAt)}` : `added ${timeAgo(h.createdAt)}${h.createdByName ? ` by ${h.createdByName}` : ''}`}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                <StatusMark state={st.state} label={st.label} />
                <Dropdown>
                  <Button size="sm" variant="ghost" isIconOnly aria-label={`More for ${h.host}`}>
                    <Ellipsis className="size-4" />
                  </Button>
                  <Dropdown.Popover placement="bottom end" className="min-w-52">
                    <Dropdown.Menu aria-label={`More for ${h.host}`} onAction={(k) => act(h, String(k))}>
                      <Dropdown.Item id="test" textValue="Send a test event">
                        <Label>Send a test event</Label>
                      </Dropdown.Item>
                      <Dropdown.Item id="deliveries" textValue="Recent deliveries">
                        <Label>Recent deliveries</Label>
                      </Dropdown.Item>
                      <Dropdown.Item id="events" textValue="Edit events">
                        <Label>Edit events…</Label>
                      </Dropdown.Item>
                      <Dropdown.Item id="toggle" textValue="Turn off or on">
                        <Label>{h.enabled ? 'Turn off' : 'Turn on'}</Label>
                      </Dropdown.Item>
                      <Dropdown.Item id="delete" textValue="Delete" variant="danger">
                        <Label>Delete…</Label>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown>
              </span>
            </li>
          );
        })}
        {hooks.length === 0 && !list.loading && <li className="px-4 py-3 text-muted">No webhooks. Add one to be told when a machine joins or leaves, a key is about to expire, or the access policy changes. Every change is also in the audit trail.</li>}
      </ul>
      {dialog && (dialog.kind === 'add' || dialog.kind === 'events') && <AddOrEdit dialog={dialog} catalogue={catalogue} onClose={() => setDialog(undefined)} onDone={list.reload} />}
      {dialog?.kind === 'deliveries' && <Deliveries hook={dialog.hook} onClose={() => setDialog(undefined)} />}
      <ConfirmDialog
        isOpen={dialog?.kind === 'delete'}
        title="Delete this webhook?"
        confirmLabel="Delete"
        danger
        onCancel={() => setDialog(undefined)}
        onConfirm={async () => {
          if (dialog?.kind === 'delete') await routes.deleteMeshWebhook(dialog.hook.id).then(() => list.reload(), (e: unknown) => setError(problemText(e)));
          setDialog(undefined);
        }}
      >
        <p>Nothing more is posted to {dialog?.kind === 'delete' ? dialog.hook.host : 'it'}. Its signing secret stops working for good.</p>
      </ConfirmDialog>
    </ListBlock></ListGroup>
  );
}
